import React from 'react';
import { Grid, TextInput, Typography } from '@ts/storybook';
import Misc from '@components/Misc';
import Slider from '@mui/material/Slider';
import { DateTimeInput, WrapperEntitySelector } from '@components/Inputs';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { IForm } from '@ts/ui-models';

interface FormProcessProps extends IForm {
  values: {
    groupTemplateId: string | null;
    processCategoryId: string | null;
    responsibleUserId: string;
    responsibleUser?: any;
    notes?: string;
    scheduledTimestamp?: string;
    realTimestamp?: string;
    scheduledDueTimestamp?: string;
    realEndTimestamp?: string;
    percentageCompletion: number;
  };
}

const _style = {
  sliderLabel: {
    fontSize: '12px',
    marginTop: '30px',
    marginLeft: '25px',
    width: '200px',
  },
  slider: {
    marginTop: '30px',
    marginBottom: '1px',
    marginLeft: '20px',
  },
  gridStartDates: { marginBottom: 40 },
  dateTypography: { marginTop: 20 },
};

const ProcessForm: React.FC<FormProcessProps> = ({ mode, values, onCallbackChange }) => {
  const isViewMode = mode === 'view';
  const isNewMode = mode === 'new';

  const handleChangeEntity = (field: string) => (id: string | null) => {
    onCallbackChange(field + 'Id')(id);
  };

  return (
    <Grid id="form-process" container spacing={2} direction={'column'} sx={{ width: '98%' }} pt={2} size={{ xs: 12, md: 6 }}>
      <Misc.NewPaperWithPadding>
        <Typography variant="h6" text="Process details" my={2} />
        <WrapperEntitySelector
          label="Process Category"
          searchComponent={generateSearchEntitySelector('processCategory')}
          value={values.processCategoryId}
          multiple={false}
          onChange={handleChangeEntity('processCategory')}
          isReadOnly={isViewMode}
        />

        <WrapperEntitySelector
          label={'Group Template'}
          searchComponent={generateSearchEntitySelector('groupTemplate')}
          isReadOnly={isViewMode}
          value={values.groupTemplateId}
          multiple={false}
          disabled={!isNewMode && !isViewMode}
          onChange={handleChangeEntity('groupTemplate')}
        />

        <WrapperEntitySelector
          label={'Assignee'}
          searchComponent={generateSearchEntitySelector('user')}
          isReadOnly={isViewMode}
          value={values.responsibleUserId}
          multiple={false}
          onChange={handleChangeEntity('responsibleUser')}
        />

        <TextInput multiline disabled={isViewMode} label="Notes" value={values.notes} onChangeValue={onCallbackChange('notes')} />
      </Misc.NewPaperWithPadding>

      <Misc.NewPaperWithPadding>
        <Typography variant="h6" text="Timeline & Progress" my={2} />
        <Grid container spacing={2}>
          <Grid size={{ xs: 6 }}>
            <Typography style={_style.dateTypography} text="Scheduled start date" />
            <DateTimeInput
              //label="Scheduled start date"
              disabled={isViewMode}
              onChange={onCallbackChange('scheduledTimestamp')}
              value={values.scheduledTimestamp}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Typography style={_style.dateTypography} text="Scheduled due date" />
            <DateTimeInput
              disabled={isViewMode}
              onChange={onCallbackChange('scheduledDueTimestamp')}
              value={values.scheduledDueTimestamp}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6 }}>
            <Typography style={_style.dateTypography} text="Real start date" />
            <DateTimeInput disabled={isViewMode} onChange={onCallbackChange('realTimestamp')} value={values.realTimestamp} />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Typography style={_style.dateTypography} text="Real end date" />
            <DateTimeInput
              disabled={isViewMode}
              onChange={onCallbackChange('realEndTimestamp')}
              value={values.realEndTimestamp}
            />
          </Grid>
        </Grid>
        <Grid container mt={2}>
          <Typography variant="h6" text={['Complete at', ' ', (values.percentageCompletion || 0) + '%']} />
          <Slider
            className="!w-[99%]"
            onChange={(e, v) => onCallbackChange('percentageCompletion')(v)}
            value={values.percentageCompletion}
            min={0}
            max={100}
            step={10}
            disabled={isViewMode}
          />
        </Grid>
      </Misc.NewPaperWithPadding>
    </Grid>
  );
};

export default ProcessForm;
